import { useState } from 'react';
import type { PossibleDuJour, ReglagesDeProposition, TacheDeJournee } from '../../partage/api/types';
import {
  AJOUTE_CE_QUE_TU_VEUX,
  C_EST_MA_JOURNEE,
  DANS_TA_JOURNEE,
  FINIRAIT_APRES_MINUIT,
  JE_CHOISIS_TITRE,
  LA_QUESTION,
  retirerDeLaJournee,
} from '../../partage/vocabulaire';
import { CreneauxDeLaJournee, CroixDuRetrait } from './CreneauxDeLaJournee';
import { dureeLisible } from './datesDeLAgenda';
import { faitesGardees } from './journeeDuMoment';
import { tempsDeLaJournee } from './journeeEnCours';
import { ListeAuChoix } from './ListeAuChoix';
import { resumeDesReponses, type Reponses } from './questionDuJour';
import { JaugeDuJour, TempsPris } from './TempsDeLaJournee';
import { useJourneeAComposer } from './useJourneeAComposer';
import './LaQuestion.css';
import './PreparerUnJour.css';
import './ProposeMoi.css';
import './JeChoisis.css';

/**
 * « Je choisis moi-même », au téléphone (chantier `telephone-journee-agenda`, ticket 03, décisions
 * d'Igor du 2026-10-05, 10 et 20 ; maquette `design/telephone.html`, piste « Journée »,
 * « Je choisis »).
 *
 * En haut, ta journée se remplit : « 30 min sur 2 h », la jauge, chaque tâche à son heure (on la
 * touche pour la fixer) avec ×, le rendez-vous du jour sur l'ambre. Dessous, les trois boutons et
 * les tâches qui leur répondent (`ListeAuChoix`), chacune avec son + : « un truc en dix minutes sur
 * tel projet, un truc en trente minutes sur un autre projet avec plus de difficulté » (Igor).
 *
 * Rien ne s'écrit avant « C'est ma journée », qui enregistre avec les réponses de la question,
 * comme « Propose-moi » ; « ‹ La question » revient sans rien écrire.
 */
export function JeChoisis({
  jour,
  reponses,
  maintenant,
  onRetour,
  onFermer,
  faites = [],
}: {
  jour: string;
  reponses: Reponses;
  /** L'heure qu'il est, arrondie par le serveur ; aujourd'hui seulement. */
  maintenant: string | null;
  onRetour: () => void;
  onFermer: (message: string) => void;
  /** Les tâches déjà faites aujourd'hui, gardées à leur heure (« Recommencer », ticket 04). */
  faites?: readonly TacheDeJournee[];
}) {
  const reglages: ReglagesDeProposition = {
    debut: reponses.debut,
    tempsMin: reponses.tempsMin,
    ou: reponses.ou,
    courage: reponses.courage,
  };
  const { tempsMin } = reglages;
  // Une journée neuve part vide : chaque tâche s'ajoute à la main.
  const [gardees] = useState(() => faitesGardees(faites));
  const faitesIci = new Set(gardees.map((c) => c.id));
  const journee = useJourneeAComposer({ jour, reglages, onFermer, depart: [], gardees, rangementFige: true });
  const { liste } = journee;
  // Les tâches ajoutées, pour leur durée et ce qu'elles sont.
  const [connues, setConnues] = useState<Map<string, PossibleDuJour>>(() => new Map());

  const { utilise, reste } = tempsDeLaJournee(liste, [...connues.values()].map((t) => [t.id, t.minutes] as const), tempsMin);

  function ajouterLaTache(p: PossibleDuJour) {
    if (journee.ajouterLaTache(p, reste)) setConnues((avant) => (avant.has(p.id) ? avant : new Map(avant).set(p.id, p)));
  }

  return (
    <section className="planifier-plein propose choisis" data-etape="choisis" aria-label={JE_CHOISIS_TITRE}>
      <button type="button" className="planifier-retour" onClick={onRetour}>
        ‹ {LA_QUESTION}
      </button>
      <h2 className="planifier-titre">{JE_CHOISIS_TITRE}</h2>
      <p className="planifier-resume">{resumeDesReponses(reponses, maintenant)}</p>

      <div className="choisis-journee" data-journee="oui">
        <div className="choisis-journee-tete">
          <span className="choisis-journee-nom">{DANS_TA_JOURNEE}</span>
          <span className="propose-compteur choisis-compteur">
            <TempsPris utilise={utilise} tempsMin={tempsMin} />
          </span>
        </div>
        <JaugeDuJour utilise={utilise} tempsMin={tempsMin} />
        {liste.length === 0 && <p className="propose-note">{AJOUTE_CE_QUE_TU_VEUX}</p>}
        {liste.length > 0 && journee.creneaux.length === 0 && <p className="propose-note">…</p>}
        <CreneauxDeLaJournee
          creneaux={journee.creneaux}
          faites={faitesIci}
          enHeure={journee.enHeure}
          onOuvrirLHeure={(id) => {
            journee.setMessage(null);
            journee.setEnHeure(id);
          }}
          onQuitterLHeure={() => journee.setEnHeure(null)}
          onFixer={(id, h) => void journee.fixerLHeure(id, h)}
          quoi={(c) => {
            const t = connues.get(c.id);
            return [dureeLisible(c.minutes), t?.dossier?.titre].filter(Boolean).join(' · ');
          }}
          gestes={(c) => (
            <button
              type="button"
              className="preparer-geste propose-geste"
              aria-label={retirerDeLaJournee(c.titre)}
              data-geste="retirer"
              onClick={() => journee.retirerLaTache(c.id)}
            >
              <CroixDuRetrait />
            </button>
          )}
        />
      </div>

      <ListeAuChoix jour={jour} ou={reglages.ou} courage={reglages.courage} pris={liste.map((c) => c.id)} reste={reste} onAjouter={ajouterLaTache} />

      <footer className="propose-pied">
        {journee.message && (
          <p className="propose-message" role="status">
            {journee.message}
          </p>
        )}
        {journee.apresMinuit && (
          <p className="propose-message" role="status">
            {FINIRAIT_APRES_MINUIT}
          </p>
        )}
        <button type="button" className="preparer-fort" disabled={!journee.pret} onClick={() => journee.enregistrer.mutate()}>
          {C_EST_MA_JOURNEE}
        </button>
      </footer>
    </section>
  );
}
